import { updateQueryString } from '@iconik-io/core/src/url/update_query_string';
import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import { AssetsHttpGetCollectionsByCollectionIdContents } from '@iconik-io/iconik-api/src/services/assets';
import { httpGetAssetsByAssetIdProxiesByProxyId } from '@iconik-io/iconik-api/src/services/files';
import {
  httpPostV2SearchNatural,
  SearchHttpPostSearch,
  SearchHttpPostV2SearchNatural,
  type SearchNaturalSearchRequest,
  type SearchNaturalSearchResponse,
  type SearchResponseFilterOutput,
  type SearchResponseFilters,
  type SearchResultOrder,
} from '@iconik-io/iconik-api/src/services/search';
import { commonGetList } from '@iconik-io/shared-lib/src/redux/common_list/thunks';
import { getEntityById } from '@iconik-io/shared-lib/src/redux/entities/utils';
import { isCancellation } from '@iconik-io/shared-lib/src/redux/middlewares/http/actions';
import { parseErrorFromEndpointResponse } from '@iconik-io/shared-lib/src/utils/handle_messages';
import { getQueryPage } from '@iconik-io/shared-lib/src/utils/url/get_query_page';
import { getQueryPerPage } from '@iconik-io/shared-lib/src/utils/url/get_query_per_page';

import { getMetadataUserFields } from '../../components/search_items/selectors';
import { PAGE_SIZE_ITEM } from '../../constants/local_storage';
import { SEARCH_PAGE_ROUTE } from '../../constants/routes';
import type { AppDispatch, GetState } from '../../store/types';
import { historyPush, historyReplace } from '../history/actions';
import { cancellable, httpCancelRequests } from '../http/actions';
import { errorMessage } from '../messages/thunks';
import { searchActions } from '../pages/search/actions';
import { SEARCH_LIST_NAME } from '../pages/search/list_slice';
import { selectIsSearchPagedByNumber } from '../pages/search/pagination';
import { getSearchItems } from '../pages/search/thunks';
import { searchFilterActions } from '../panels/search_filters/actions';
import { SEARCH_QUERY_FILTER_KEY } from '../search/active_filter_chips';
import {
  NATURAL_SEARCH_MAX_PER_PAGE,
  NATURAL_SEARCH_MAX_SUB_ITEMS,
  NATURAL_SEARCH_QUERY_PARAM,
  NATURAL_SEARCH_RESULT_UNIT,
} from './constants';
import { setNaturalSearchPanelQuery } from './panel_query';
import {
  type NaturalSearchProjection,
  panelToNaturalFilters,
} from './panel_to_replay';
import {
  selectIsNaturalSearchActive,
  selectIsNaturalSearchMode,
  selectNaturalSearchCollectionScope,
  selectNaturalSearchQuery,
} from './selectors';
import {
  clearNaturalSearch,
  reapplyNaturalSearchFilter,
  setNaturalSearchFilterPaused,
  setNaturalSearchInterpretation,
  setNaturalSearchLoading,
  setNaturalSearchOrder,
  setNaturalSearchProxyUrl,
  setNaturalSearchResult,
} from './slice';
import { readStoredNaturalSearch } from './storage';
import type {
  NaturalSearchReplay,
  NaturalSearchSession,
  SearchNaturalSearchSegment,
} from './types';
import { naturalFiltersToPanelValues, naturalFiltersToQuery } from './utils';

const getNaturalSearchUrl = (query: string | null): string =>
  `${SEARCH_PAGE_ROUTE}${updateQueryString('', {
    [NATURAL_SEARCH_QUERY_PARAM]: query,
  })}`;

/**
 * Replaces the whole query string rather than editing it: the two search flows
 * never hold the grid at once, so the other's params are always stale. Clearing
 * replaces the entry instead of pushing, since a dismissal is not a navigation.
 */
const setSearchPageQuery =
  (query: string | null) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const target = getNaturalSearchUrl(query);
    const { pathname, search } = getState().router.location;
    if (`${pathname}${search}` === target) {
      return;
    }
    dispatch((query ? historyPush : historyReplace)(target));
  };

type NaturalSearchOutcome = 'published' | 'superseded' | 'failed';

const DEFAULT_ERROR_MESSAGE = 'Error running natural search. Please try again.';

const getErrorMessage = (error: unknown): string => {
  const messages =
    error instanceof EndpointError
      ? parseErrorFromEndpointResponse(error.response)
      : [];
  return messages.join(' ') || DEFAULT_ERROR_MESSAGE;
};

/** The page size a natural search asks for, from the URL or the stored preference. */
const getPerPage = (search: string): number =>
  Math.min(
    getQueryPerPage(search, PAGE_SIZE_ITEM),
    NATURAL_SEARCH_MAX_PER_PAGE,
  );

/**
 * What a replay keeps of the session: the readings of the fresh answer, which
 * a replay does not report, and the user's adjustments to it.
 */
type KeptFromSession = Pick<
  NaturalSearchSession,
  'interpretations' | 'adjustments'
>;

const NOTHING_KEPT: KeptFromSession = {
  interpretations: { list: [], active: null },
  adjustments: { added: [], paused: [] },
};

/**
 * What a request is. A fresh one has the LLM read the query, and its answer
 * starts a new session. A replay asks for more of the answer the session
 * holds, with no LLM call, and keeps what the session knew about it.
 */
type NaturalSearchRun =
  | { kind: 'fresh' }
  | {
      kind: 'replay';
      replay: NaturalSearchReplay;
      /** The filters the replay sent: the answer's, as the user now has them. */
      applied: SearchResponseFilterOutput[];
      /**
       * How the panel holds the answer, or null when the panel is to be filled
       * from it, as after a reload of the tab.
       */
      projection: NaturalSearchProjection | null;
      kept: KeptFromSession;
    };

/**
 * Puts an answer's words and filters in the panel, and returns how, for a
 * replay to tell what the user changed there.
 *
 * The words, not the whole question: its other parts are the filters beside
 * them. The filters replace the panel's rather than merge with them, since the
 * request did not send those. An answer without `filters` is a partial one,
 * not one without filters, so the panel's are left alone rather than wiped.
 */
const fillPanelFromAnswer =
  (query: string, filters: SearchResponseFilters | undefined) =>
  (
    dispatch: AppDispatch,
    getState: GetState,
  ): NaturalSearchProjection | null => {
    dispatch(
      setNaturalSearchPanelQuery(
        filters ? naturalFiltersToQuery(filters.applied) : query,
      ),
    );
    if (!filters) {
      return null;
    }
    const state = getState();
    const panelValues = naturalFiltersToPanelValues(filters.applied, {
      facetFieldNames: new Set(
        (state.settings.merged.facet_fields ?? []).map(({ name }) => name),
      ),
      metadataFields: getMetadataUserFields(state),
    });
    dispatch(searchFilterActions.setFilterValues(panelValues.filterValues));
    dispatch(searchFilterActions.setFacetValues(panelValues.facetValues));
    // A value paused on the previous answer would otherwise hide one this
    // answer decided.
    dispatch(searchFilterActions.setDisabledFilters([]));
    return {
      applied: filters.applied ?? [],
      query: naturalFiltersToQuery(filters.applied),
      ...panelValues,
    };
  };

/**
 * Matched moments per result. At an asset or collection grain each object is a
 * document carrying its moments; at a segment grain the objects are the
 * moments themselves.
 */
const getSegmentsById = (
  objects: SearchNaturalSearchResponse['objects'],
): Record<string, SearchNaturalSearchSegment[]> => {
  const segmentsById: Record<string, SearchNaturalSearchSegment[]> = {};
  for (const object of objects ?? []) {
    if ('segments' in object && object.id && object.segments?.length) {
      segmentsById[object.id] = object.segments;
    }
  }
  return segmentsById;
};

/**
 * Sends one natural-search request into the search list and publishes what it
 * answered. With `append`, the results are the next page of the answer on
 * screen, added under it as the user scrolls.
 */
const requestNaturalSearch =
  (
    query: string,
    request: SearchNaturalSearchRequest,
    run: NaturalSearchRun,
    {
      debounce = false,
      append = false,
    }: { debounce?: boolean; append?: boolean } = {},
  ) =>
  async (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<NaturalSearchOutcome> => {
    // Both searches fill the same list, so a regular search still in flight
    // would overwrite these results on arrival.
    dispatch(
      httpCancelRequests([
        SearchHttpPostSearch,
        AssetsHttpGetCollectionsByCollectionIdContents,
      ]),
    );
    // Scrolled results are replaced rather than paged over, so they go first,
    // as for a regular search, and the grid shows the whole list loading.
    if (!append && !selectIsSearchPagedByNumber(getState())) {
      dispatch(searchActions.resetItems());
    }
    dispatch(setNaturalSearchLoading(true));

    let body: SearchNaturalSearchResponse;
    try {
      const response = await commonGetList({
        listName: SEARCH_LIST_NAME,
        dispatch,
        params: { page: request.page },
        config: {
          debounce,
          paginationType: append ? 'infinite' : 'regular',
          debounceDependencies: [
            SearchHttpPostV2SearchNatural,
            SearchHttpPostSearch,
            AssetsHttpGetCollectionsByCollectionIdContents,
          ],
        },
        getList: () => dispatch(cancellable(httpPostV2SearchNatural(request))),
      });
      // A superseded request resolves undefined; the newer one owns the list.
      if (!response) {
        return 'superseded';
      }
      body = response.body;
    } catch (error) {
      if (isCancellation(error)) {
        // The loading flag belongs to whichever request superseded this one.
        return 'superseded';
      }
      // The list no longer holds the session's results either way. The answer
      // a replay failed on can still be asked again, but a question that got
      // no answer leaves the session describing an earlier one.
      dispatch(
        run.kind === 'fresh'
          ? clearNaturalSearch()
          : setNaturalSearchLoading(false),
      );
      dispatch(errorMessage({ text: getErrorMessage(error) }));
      return 'failed';
    }

    // The search was cleared while it was in flight (mode switch or clear),
    // so publishing now would resurrect results the user just dismissed.
    if (!getState().naturalSearch.isLoading) {
      return 'superseded';
    }

    let filters: SearchResponseFilters | undefined;
    let projection: NaturalSearchProjection | null;
    let kept: KeptFromSession;
    if (run.kind === 'fresh') {
      filters = body.filters;
      projection = dispatch(fillPanelFromAnswer(query, filters));
      const list = body.interpretations ?? [];
      const answered = list.findIndex((reading) => reading.answered);
      kept = {
        interpretations: { list, active: answered === -1 ? null : answered },
        adjustments: NOTHING_KEPT.adjustments,
      };
    } else {
      // A replay runs one round, so what it reports of the reading (such as
      // what it relaxed) is less than the answer's: only its order is kept,
      // which a replay without words turns to recency whatever it was asked.
      filters = {
        ...run.replay.filters,
        applied: run.applied,
        order: body.filters?.order ?? run.replay.filters.order,
      };
      projection =
        run.projection ?? dispatch(fillPanelFromAnswer(query, filters));
      kept = run.kept;
    }

    // Asked again on every answer: the same reading can page on one request
    // and not on the next, e.g. once a filter reaches a backend that cannot.
    const pagination = {
      supported: body.pagination?.supported ?? false,
      reason: body.pagination?.reason ?? null,
    };
    if (!pagination.supported) {
      // The answer is this one page, whatever the backends counted beyond it.
      const { ids, pagination: listPagination } = getState().pages.search.list;
      dispatch(
        searchActions.setPagination({
          page: 1,
          pages: 1,
          total: ids.length,
          per_page: listPagination.per_page,
        }),
      );
    }

    const replay = filters ? { filters, pagination } : null;
    const { ids, pagination: listPagination } = getState().pages.search.list;
    const segmentsById = getSegmentsById(body.objects);
    const previous = getState().naturalSearch.session;
    dispatch(
      setNaturalSearchResult({
        query,
        replay,
        projection: replay ? projection : null,
        interpretations: kept.interpretations,
        adjustments: kept.adjustments,
        status: {
          matchQuality: body.match_quality ?? null,
          degraded: body.degraded ?? false,
        },
        results: { ids, pagination: listPagination },
        segmentsById:
          append && previous
            ? { ...previous.segmentsById, ...segmentsById }
            : segmentsById,
      }),
    );
    return 'published';
  };

/**
 * Runs a natural-language search into the regular search list. The endpoint
 * answers in the same document shape as `POST /v2/search/`, so the response
 * normalizes into `search_items` and the results render, select and navigate
 * like any other search's; the session is what marks them as natural ones.
 */
export const runNaturalSearch =
  (
    query: string,
    {
      debounce = false,
      pinned = [],
    }: {
      debounce?: boolean;
      /** Filters the new reading of the query must keep. */
      pinned?: SearchResponseFilterOutput[];
    } = {},
  ) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<boolean> => {
    const trimmed = query.trim();
    if (!trimmed) {
      return false;
    }

    // Results only render on the search page, so go there first. Navigating up
    // front also means the loading state is visible while the query runs.
    const wasElsewhere =
      getState().router.location.pathname !== SEARCH_PAGE_ROUTE;
    dispatch(setSearchPageQuery(trimmed));

    const outcome = await dispatch(
      requestNaturalSearch(
        trimmed,
        {
          query: trimmed,
          ...(pinned.length ? { filters: pinned } : {}),
          result_unit: NATURAL_SEARCH_RESULT_UNIT,
          nested_results: true,
          max_sub_items: NATURAL_SEARCH_MAX_SUB_ITEMS,
          // The empty search string skips the URL, which at this point may
          // still be the page searched from.
          per_page: getPerPage(''),
        },
        { kind: 'fresh' },
        { debounce },
      ),
    );
    if (outcome === 'failed' && wasElsewhere) {
      // Otherwise the freshly navigated grid is left blank.
      dispatch(getSearchItems(true));
    }
    return outcome === 'published';
  };

// A page change reaches here both from its own caller and from the grid reacting
// to the URL, and the second request would only cancel the first.
let replayInFlight: string | null = null;

/**
 * Shows the page of an answer the URL asks for by replaying the reading that
 * answered: no LLM call, so every page belongs to the same answer. An answer
 * that cannot page is only ever asked for its first page. Under infinite scroll
 * the URL names no page: a replay shows the first one, and `nextPage` adds the
 * one after the results on screen.
 *
 * With a projection, the reading is replayed as the panel now describes it, so
 * a filter the user changed there applies to the same answer. Without one, the
 * panel is about to be filled from the reading instead.
 */
const replayNaturalSearch =
  (
    query: string,
    replay: NaturalSearchReplay,
    {
      projection = null,
      kept = NOTHING_KEPT,
      debounce = false,
      nextPage,
    }: {
      projection?: NaturalSearchProjection | null;
      kept?: KeptFromSession;
      debounce?: boolean;
      nextPage?: number;
    } = {},
  ) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<boolean> => {
    const { filters } = replay;
    // The server numbers every block it answers with, and one it cannot tell
    // the version of is not a reading it can replay.
    if (filters.version === undefined) {
      return dispatch(runNaturalSearch(query, { debounce }));
    }
    const { pathname, search } = getState().router.location;
    const isPagedByUrl =
      nextPage === undefined && selectIsSearchPagedByNumber(getState());
    let page = nextPage ?? (isPagedByUrl ? getQueryPage(search) : 1);
    if (isPagedByUrl && page > 1 && !replay.pagination.supported) {
      page = 1;
      dispatch(
        historyReplace(
          `${pathname}${updateQueryString(search, { page: null })}`,
        ),
      );
    }
    const perPage = getPerPage(search);
    const applied = projection
      ? panelToNaturalFilters(
          projection,
          getState().panelsState.searchFilters,
          kept.adjustments,
        )
      : (replay.filters.applied ?? []);

    const key = JSON.stringify([query, page, perPage, applied]);
    if (replayInFlight === key) {
      return false;
    }
    replayInFlight = key;

    try {
      const outcome = await dispatch(
        requestNaturalSearch(
          query,
          {
            query,
            // The block goes back whole, as it came, so whatever the server
            // added to it for a replay still reaches it.
            replay: {
              ...filters,
              version: filters.version,
              applied,
            },
            // Without it the replay would present at the ranking grain, which
            // turns a list of assets into one of moments.
            result_unit: filters.result_unit ?? NATURAL_SEARCH_RESULT_UNIT,
            nested_results: true,
            max_sub_items: NATURAL_SEARCH_MAX_SUB_ITEMS,
            per_page: perPage,
            page,
          },
          { kind: 'replay', replay, applied, projection, kept },
          { debounce, append: nextPage !== undefined },
        ),
      );
      if (outcome !== 'published' || !isPagedByUrl || page === 1) {
        return outcome === 'published';
      }
      // Paged before, but not any more: what came back is the whole answer.
      const { session } = getState().naturalSearch;
      if (session?.replay && !session.replay.pagination.supported) {
        const location = getState().router.location;
        dispatch(
          historyReplace(
            `${location.pathname}${updateQueryString(location.search, {
              page: null,
            })}`,
          ),
        );
      }
      return true;
    } finally {
      // However the replay ended, so that a failed one can be asked again.
      if (replayInFlight === key) {
        replayInFlight = null;
      }
    }
  };

/**
 * Fetches natural-search results again, as the panel and the URL now describe
 * them: the page the URL asks for, of the same answer, with the filters the
 * panel holds. New words in the query are a new question, which the LLM reads
 * afresh.
 * No-op when no answer can be replayed.
 */
export const refreshNaturalSearch =
  ({ debounce = false }: { debounce?: boolean } = {}) =>
  (dispatch: AppDispatch, getState: GetState): Promise<boolean> => {
    const state = getState();
    const { session } = state.naturalSearch;
    if (!session?.replay) {
      return Promise.resolve(false);
    }
    const { query, disabledFilters = [] } = state.panelsState.searchFilters;
    const text = query.trim();
    // Without a projection, the panel is about to be filled from the reading
    // instead, so what it holds now is not the user's.
    if (
      session.projection &&
      text &&
      text !== session.projection.query &&
      !disabledFilters.includes(SEARCH_QUERY_FILTER_KEY)
    ) {
      return dispatch(runNaturalSearch(text, { debounce }));
    }
    return dispatch(
      replayNaturalSearch(session.query, session.replay, {
        projection: session.projection,
        kept: session,
        debounce,
      }),
    );
  };

/**
 * Adds the next page of the answer on screen under its results, for infinite
 * scroll. No-op while a request runs, or once the answer is all on screen.
 */
export const loadMoreNaturalSearch =
  () =>
  (dispatch: AppDispatch, getState: GetState): Promise<boolean> => {
    const state = getState();
    const { isLoading, session } = state.naturalSearch;
    const { page, pages } = state.pages.search.list.pagination;
    if (isLoading || !session?.replay?.pagination.supported || page >= pages) {
      return Promise.resolve(false);
    }
    return dispatch(
      replayNaturalSearch(session.query, session.replay, {
        projection: session.projection,
        kept: session,
        nextPage: page + 1,
      }),
    );
  };

/** Shows the natural-search answer in another order, as more of the same answer. */
export const sortNaturalSearch =
  (order: SearchResultOrder) =>
  (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(setNaturalSearchOrder(order));
    return dispatch(refreshNaturalSearch());
  };

/**
 * Shows the answer of another reading of the query the LLM considered, from its
 * first page, without asking it again.
 */
export const switchNaturalSearchInterpretation =
  (index: number) =>
  (dispatch: AppDispatch, getState: GetState): Promise<boolean> => {
    dispatch(setNaturalSearchInterpretation(index));
    const { pathname, search } = getState().router.location;
    if (getQueryPage(search) > 1) {
      dispatch(
        historyPush(`${pathname}${updateQueryString(search, { page: null })}`),
      );
    }
    return dispatch(refreshNaturalSearch());
  };

/** Applies a filter the answer gave up on, and replays the answer with it. */
export const reapplyNaturalSearchRelaxedFilter =
  (entry: SearchResponseFilterOutput) =>
  (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(reapplyNaturalSearchFilter(entry));
    return dispatch(refreshNaturalSearch());
  };

/**
 * Pauses, or resumes, a filter of the answer the panel does not show, and
 * replays the answer as it now stands.
 */
export const setNaturalSearchFilterPausedAndRefresh =
  (entry: SearchResponseFilterOutput, paused: boolean) =>
  (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(setNaturalSearchFilterPaused({ entry, paused }));
    return dispatch(refreshNaturalSearch());
  };

/**
 * Runs a natural search from the search bar, either in the collections the
 * answer on screen searched in or in all of Iconik. Switching between the two
 * on the same question replays its answer with those collections paused or
 * resumed; anything else asks the LLM again, keeping them only when asked to.
 */
export const runNaturalSearchInCollections =
  (query: string, inCollections: boolean) =>
  (dispatch: AppDispatch, getState: GetState): Promise<boolean> => {
    const trimmed = query.trim();
    const scope = selectNaturalSearchCollectionScope(getState());
    if (!scope) {
      return dispatch(runNaturalSearch(trimmed));
    }
    const isSwitchingScope =
      trimmed === selectNaturalSearchQuery(getState()) &&
      inCollections === scope.isPaused;
    if (!isSwitchingScope) {
      return dispatch(
        runNaturalSearch(trimmed, {
          pinned: inCollections ? scope.entries : [],
        }),
      );
    }
    for (const entry of scope.entries) {
      dispatch(setNaturalSearchFilterPaused({ entry, paused: !inCollections }));
    }
    return dispatch(refreshNaturalSearch());
  };

/**
 * Shows the natural search a landing URL asks for, if it asks for one and the
 * user is in the mode that serves it. Returns whether it did, so the caller
 * knows whether the grid still needs its own load.
 *
 * Coming back to results this session already holds (from an asset opened
 * among them) shows them again as they were, and a reload of the tab replays
 * the answer it had. Anything else runs the query again, from its first page:
 * the LLM may read it differently now, so no page of the earlier answer can be
 * assumed to exist.
 */
export const restoreNaturalSearchFromUrl =
  () =>
  (dispatch: AppDispatch, getState: GetState): boolean => {
    const state = getState();
    const query = state.routeParams.query[NATURAL_SEARCH_QUERY_PARAM];
    if (!query || !selectIsNaturalSearchMode(state)) {
      return false;
    }
    const { isLoading, session } = state.naturalSearch;
    // The search that navigated here is already on its way.
    if (isLoading) {
      return true;
    }
    if (session?.query === query) {
      dispatch(searchActions.setIds(session.results.ids));
      dispatch(searchActions.setPagination(session.results.pagination));
      dispatch(searchActions.setStatus('success'));
      return true;
    }
    const stored = readStoredNaturalSearch();
    if (stored?.query === query) {
      // The panel was lost with the reload, so it is filled from the reading.
      dispatch(
        replayNaturalSearch(stored.query, stored.replay, {
          kept: {
            interpretations:
              stored.interpretations ?? NOTHING_KEPT.interpretations,
            adjustments: stored.adjustments ?? NOTHING_KEPT.adjustments,
          },
        }),
      );
      return true;
    }
    // Replaced rather than pushed by the search below: dropping a page that
    // does not exist is not a navigation.
    const target = getNaturalSearchUrl(query);
    const { pathname, search } = state.router.location;
    if (`${pathname}${search}` !== target) {
      dispatch(historyReplace(target));
    }
    dispatch(runNaturalSearch(query));
    return true;
  };

/**
 * Clears the results, abandons any search still in flight and hands the grid
 * back to the regular search. Without the cancel the request would keep running
 * and its result would be discarded on arrival, wasting an expensive AI call.
 *
 * The refetch is not optional once a natural search has owned the grid: the
 * list still holds its results, which nothing would describe any more once the
 * session is gone. Otherwise there is nothing to hand back, and refetching
 * would escalate the caller's own reload into the input debounce.
 */
export const clearNaturalSearchAndCancel =
  () =>
  (dispatch: AppDispatch, getState: GetState): void => {
    // Availability, the search page, and a search that ran: exactly the state
    // in which the list holds natural results.
    const wasOwningTheGrid = selectIsNaturalSearchActive(getState());
    dispatch(httpCancelRequests([SearchHttpPostV2SearchNatural]));
    dispatch(clearNaturalSearch());
    if (!wasOwningTheGrid) {
      return;
    }
    dispatch(searchFilterActions.setQuery(''));
    // The refetch below builds its criteria from these, so leftover filters the
    // AI derived would silently scope the grid we just handed back.
    dispatch(searchFilterActions.setFilterValues({}));
    dispatch(searchFilterActions.setFacetValues({}));
    dispatch(searchFilterActions.setDisabledFilters([]));
    dispatch(setSearchPageQuery(null));
    dispatch(getSearchItems(true));
  };

// The store only records resolved URLs, so a second hover arriving before the
// first response would issue the same request again.
const proxyRequestsInFlight = new Set<string>();

/**
 * Lazily resolves a playable proxy URL for an asset so the hover frame preview
 * can seek into the underlying video. No-op if already resolved or in flight.
 */
export const fetchNaturalSearchPreviewProxy =
  (assetId: string) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const state = getState();
    const session = state.naturalSearch.session;
    if (
      !session ||
      assetId in session.proxyUrlByAssetId ||
      proxyRequestsInFlight.has(assetId)
    ) {
      return;
    }

    // search_items are stored normalized, so proxies are ids into the proxies
    // entity map — any of them identifies a playable rendition.
    const document = getEntityById(state, 'search_items', assetId);
    const proxyId = document?.proxies?.[0];
    if (!proxyId) {
      dispatch(setNaturalSearchProxyUrl({ assetId, url: null }));
      return;
    }

    proxyRequestsInFlight.add(assetId);
    try {
      const response = await dispatch(
        httpGetAssetsByAssetIdProxiesByProxyId(assetId, proxyId),
      );
      dispatch(
        setNaturalSearchProxyUrl({
          assetId,
          url: response.body?.url ?? null,
        }),
      );
    } catch {
      dispatch(setNaturalSearchProxyUrl({ assetId, url: null }));
    } finally {
      proxyRequestsInFlight.delete(assetId);
    }
  };
